'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type * as React from 'react';
import { useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { UserIdentity } from '@/components/profile/user-identity';
import { ThesisActions } from '@/components/social/debate/thesis-actions';
import { ThesisMarketAvatar } from '@/components/social/debate/thesis-market-avatar';
import { ThesisReplyThread } from '@/components/social/debate/thesis-reply-thread';
import { READ_ONLY_THESIS_REPLY_WRITE_ACCESS, type ThesisReplyWriteAccess } from '@/components/social/debate/thesis-reply-write-access';
import { SolscanLink } from '@/components/social/shared/solscan-link';
import type { ThesisItem } from '@/components/social/types';
import { Icon } from '@/components/ui/icon';
import { MinusSignCircleIconSolid, PlusSignCircleIconSolid } from '@/components/ui/icon/glyphs';
import { resolveProposalCategory } from '@/config/proposal-display';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { useMarketDetailPrefetchIntent, useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FALLBACK_CATEGORY_EMOJI = '\u{1F4A1}';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Variant = 'debate' | 'portfolio';
interface ThesisCardProps {
  thesis: ThesisItem;
  isAuthenticated: boolean;
  userId: string | null;
  /** Shared market-level access controls writing only; threads stay public. */
  replyWriteAccess?: ThesisReplyWriteAccess;
  /** Opens the owning market's mobile trade sheet. */
  onOpenTradeForm?: () => void;
  /**
   * Discriminator between the two surfaces (spec 094):
   *
   * - `'debate'` (default) — rendered inside the market-detail Debate tab.
   *   Profile-picture avatar that routes to `/profile/{userId}`. The card body
   *   is not pressable; the viewer is already on the parent market. Replies
   *   expand inline.
   * - `'portfolio'` — rendered inside the portfolio Theses panel. Rounded
   *   card surface with an absolute-positioned `<Link>` overlay to the
   *   market's Debate tab anchored to this thesis. Avatar is a rounded-square
   *   market image with an outcome-colored status dot. Parent market title
   *   above each card uses the same typography as portfolio PositionRow market titles.
   *   Replies do not expand inline (the viewer is one tap away from the destination's full thread).
   */
  variant?: Variant;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ThesisCard(t0) {
  const $ = _c(129);
  const {
    thesis,
    isAuthenticated,
    userId,
    replyWriteAccess: t1,
    onOpenTradeForm,
    variant: t2
  } = t0;
  const replyWriteAccess = t1 === undefined ? READ_ONLY_THESIS_REPLY_WRITE_ACCESS : t1;
  const variant = t2 === undefined ? "debate" : t2;
  const locale = useLocale();
  const [showReplies, setShowReplies] = useState(false);
  const [replyCount, setReplyCount] = useState(thesis.replyCount);
  const tTimeIntl = useTranslations("time");
  const tDebate = useTranslations("social.debate");
  const {
    formatD6
  } = useFormattedCurrency();
  let t3;
  if ($[0] !== tTimeIntl) {
    t3 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const tTime = t3;
  const t4 = thesis.outcomeLabel ?? "";
  let t5;
  if ($[2] !== t4 || $[3] !== thesis.outcomeIndex) {
    t5 = {
      title: t4,
      index: thesis.outcomeIndex
    };
    $[2] = t4;
    $[3] = thesis.outcomeIndex;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    hue
  } = useOutcomeColor(t5);
  const isBuy = thesis.tradeDirection === "buy";
  let t6;
  if ($[5] !== isBuy || $[6] !== tDebate) {
    t6 = isBuy ? tDebate("bought") : tDebate("sold");
    $[5] = isBuy;
    $[6] = tDebate;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const verb = t6;
  const outcomeLabel = thesis.outcomeLabel ?? "";
  let t7;
  if ($[8] !== formatD6 || $[9] !== thesis.tradeAmount) {
    t7 = formatD6(thesis.tradeAmount);
    $[8] = formatD6;
    $[9] = thesis.tradeAmount;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const tradeLineText = `${verb} ${outcomeLabel} for ${t7}`;
  const traderHref = `/portfolio/${thesis.trader.userId ?? ""}`;
  const marketHref = `/markets/${thesis.marketPubkey}`;
  const deepLinkHref = `/markets/${thesis.marketPubkey}?tab=debate&thesis=${thesis.signature}`;
  const traderIntent = useUserProfilePrefetchIntent(thesis.trader.userId);
  const marketIntent = useMarketDetailPrefetchIntent(thesis.marketPubkey);
  const deepLinkIntent = useIntentRoutePrefetch(deepLinkHref, marketIntent.onMouseEnter);
  const categoryEmoji = thesis.marketCategory.length > 0 ? resolveProposalCategory(thesis.marketCategory)?.emoji ?? FALLBACK_CATEGORY_EMOJI : FALLBACK_CATEGORY_EMOJI;
  let t8;
  if ($[11] !== replyWriteAccess || $[12] !== showReplies) {
    t8 = function handleToggleReplies() {
      if (!showReplies && replyWriteAccess.status === "ineligible") {
        replyWriteAccess.refresh();
      }
      setShowReplies(_temp);
    };
    $[11] = replyWriteAccess;
    $[12] = showReplies;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleToggleReplies = t8;
  let t9;
  if ($[14] !== thesis.trader) {
    t9 = <UserIdentity user={thesis.trader} size="md" nameClassName="text-foreground-secondary" handleClassName="text-body text-foreground-tertiary" />;
    $[14] = thesis.trader;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9 || $[17] !== traderHref || $[18] !== traderIntent) {
    t10 = <Link href={traderHref} prefetch={false} className="relative z-10 min-w-0 truncate hover:underline" {...traderIntent} onClick={_temp2}>{t9}</Link>;
    $[16] = t9;
    $[17] = traderHref;
    $[18] = traderIntent;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="shrink-0 text-body text-foreground-tertiary">·</span>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== locale || $[22] !== tTime || $[23] !== thesis.createdAt) {
    t12 = formatRelativeTime(thesis.createdAt, tTime, {
      locale
    });
    $[21] = locale;
    $[22] = tTime;
    $[23] = thesis.createdAt;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = <span className="shrink-0 text-body text-foreground-tertiary">{t12}</span>;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="shrink-0 text-body text-foreground-tertiary">·</span>;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== thesis.signature) {
    t15 = <SolscanLink signature={thesis.signature} variant="inline" className="relative z-10" />;
    $[28] = thesis.signature;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t10 || $[31] !== t13 || $[32] !== t15) {
    t16 = <div className="flex flex-wrap items-center gap-1">{t10}{t11}{t13}{t14}{t15}</div>;
    $[30] = t10;
    $[31] = t13;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const metadataRow = t16;
  const t17 = `text-outcome-${hue}-text`;
  let t18;
  if ($[34] !== t17) {
    t18 = cn("text-body tabular-nums", t17);
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t18 || $[37] !== tradeLineText) {
    t19 = <p className={t18}>{tradeLineText}</p>;
    $[36] = t18;
    $[37] = tradeLineText;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const tradeLine = t19;
  let t20;
  if ($[39] !== thesis.content) {
    t20 = <p className="text-body leading-snug">{thesis.content}</p>;
    $[39] = thesis.content;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const body = t20;
  if (variant === "debate") {
    const t21 = `thesis-${thesis.signature}`;
    const t22 = thesis.trader.avatarNsfw ?? false;
    const t23 = thesis.trader.avatarPreferences?.rasterise ?? false;
    let t24;
    if ($[41] !== t22 || $[42] !== t23 || $[43] !== thesis.trader.avatarUrl || $[44] !== thesis.trader.displayName || $[45] !== thesis.trader.userId) {
      t24 = <UserAvatar userId={thesis.trader.userId} displayName={thesis.trader.displayName} avatarUrl={thesis.trader.avatarUrl} nsfw={t22} rasterise={t23} size="md" />;
      $[41] = t22;
      $[42] = t23;
      $[43] = thesis.trader.avatarUrl;
      $[44] = thesis.trader.displayName;
      $[45] = thesis.trader.userId;
      $[46] = t24;
    } else {
      t24 = $[46];
    }
    let t25;
    if ($[47] !== isBuy || $[48] !== tDebate) {
      t25 = isBuy ? tDebate("bought") : tDebate("sold");
      $[47] = isBuy;
      $[48] = tDebate;
      $[49] = t25;
    } else {
      t25 = $[49];
    }
    const t26 = isBuy ? "text-feedback-success-accent" : "text-feedback-error-accent";
    let t27;
    if ($[50] !== t26) {
      t27 = cn("absolute -right-1 -bottom-1 rounded-full bg-background-secondary p-px", t26);
      $[50] = t26;
      $[51] = t27;
    } else {
      t27 = $[51];
    }
    const t28 = isBuy ? PlusSignCircleIconSolid : MinusSignCircleIconSolid;
    let t29;
    if ($[52] !== t28) {
      t29 = <Icon icon={t28} className="size-4" aria-hidden={true} />;
      $[52] = t28;
      $[53] = t29;
    } else {
      t29 = $[53];
    }
    let t30;
    if ($[54] !== t25 || $[55] !== t27 || $[56] !== t29) {
      t30 = <span role="img" aria-label={t25} className={t27}>{t29}</span>;
      $[54] = t25;
      $[55] = t27;
      $[56] = t29;
      $[57] = t30;
    } else {
      t30 = $[57];
    }
    let t31;
    if ($[58] !== t24 || $[59] !== t30) {
      t31 = <span className="relative block size-10">{t24}{t30}</span>;
      $[58] = t24;
      $[59] = t30;
      $[60] = t31;
    } else {
      t31 = $[60];
    }
    let t32;
    if ($[61] !== t31 || $[62] !== traderHref || $[63] !== traderIntent) {
      t32 = <Link href={traderHref} prefetch={false} className="shrink-0" {...traderIntent}>{t31}</Link>;
      $[61] = t31;
      $[62] = traderHref;
      $[63] = traderIntent;
      $[64] = t32;
    } else {
      t32 = $[64];
    }
    let t33;
    if ($[65] !== body || $[66] !== metadataRow || $[67] !== tradeLine) {
      t33 = <div className="flex min-w-0 flex-1 flex-col gap-1">{metadataRow}{tradeLine}{body}</div>;
      $[65] = body;
      $[66] = metadataRow;
      $[67] = tradeLine;
      $[68] = t33;
    } else {
      t33 = $[68];
    }
    let t34;
    if ($[69] !== t32 || $[70] !== t33) {
      t34 = <div className="flex gap-3">{t32}{t33}</div>;
      $[69] = t32;
      $[70] = t33;
      $[71] = t34;
    } else {
      t34 = $[71];
    }
    let t35;
    if ($[72] !== handleToggleReplies || $[73] !== isAuthenticated || $[74] !== replyCount || $[75] !== thesis) {
      t35 = <div className="pl-[52px]"><ThesisActions thesis={thesis} replyCount={replyCount} isAuthenticated={isAuthenticated} onToggleReplies={handleToggleReplies} /></div>;
      $[72] = handleToggleReplies;
      $[73] = isAuthenticated;
      $[74] = replyCount;
      $[75] = thesis;
      $[76] = t35;
    } else {
      t35 = $[76];
    }
    let t36;
    if ($[77] !== isAuthenticated || $[78] !== onOpenTradeForm || $[79] !== replyWriteAccess || $[80] !== showReplies || $[81] !== thesis.marketPubkey || $[82] !== thesis.signature || $[83] !== userId) {
      t36 = showReplies ? <div className="contents md:block md:pl-[52px]"><ThesisReplyThread thesisSignature={thesis.signature} marketPubkey={thesis.marketPubkey} isAuthenticated={isAuthenticated} userId={userId} writeAccess={replyWriteAccess} onClose={() => setShowReplies(false)} {...onOpenTradeForm !== undefined ? {
          onOpenTradeForm
        } : {}} onReplyAdded={() => setReplyCount(_temp3)} onReplyRemoved={() => setReplyCount(_temp4)} /></div> : null;
      $[77] = isAuthenticated;
      $[78] = onOpenTradeForm;
      $[79] = replyWriteAccess;
      $[80] = showReplies;
      $[81] = thesis.marketPubkey;
      $[82] = thesis.signature;
      $[83] = userId;
      $[84] = t36;
    } else {
      t36 = $[84];
    }
    let t37;
    if ($[85] !== t21 || $[86] !== t34 || $[87] !== t35 || $[88] !== t36) {
      t37 = <div id={t21} className="flex flex-col gap-2 px-0 pt-3 pb-2">{t34}{t35}{t36}</div>;
      $[85] = t21;
      $[86] = t34;
      $[87] = t35;
      $[88] = t36;
      $[89] = t37;
    } else {
      t37 = $[89];
    }
    return t37;
  }
  const t21 = `thesis-${thesis.signature}`;
  let t22;
  if ($[90] !== deepLinkHref || $[91] !== deepLinkIntent || $[92] !== thesis.marketTitle) {
    t22 = <Link href={deepLinkHref} prefetch={false} aria-label={thesis.marketTitle} className="absolute inset-0 z-0 rounded-panel-row focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-primary" {...deepLinkIntent} />;
    $[90] = deepLinkHref;
    $[91] = deepLinkIntent;
    $[92] = thesis.marketTitle;
    $[93] = t22;
  } else {
    t22 = $[93];
  }
  let t23;
  if ($[94] !== thesis.marketTitle) {
    t23 = <p className="truncate text-body font-medium text-foreground-primary" title={thesis.marketTitle}>{thesis.marketTitle}</p>;
    $[94] = thesis.marketTitle;
    $[95] = t23;
  } else {
    t23 = $[95];
  }
  let t24;
  if ($[96] !== categoryEmoji || $[97] !== hue || $[98] !== outcomeLabel || $[99] !== thesis.marketPubkey || $[100] !== thesis.outcomeImageNsfw || $[101] !== thesis.outcomeImageUrl || $[102] !== thesis.outcomeIndex) {
    t24 = <ThesisMarketAvatar marketPubkey={thesis.marketPubkey} outcomeIndex={thesis.outcomeIndex} outcomeTitle={outcomeLabel} outcomeImageUrl={thesis.outcomeImageUrl} outcomeImageNsfw={thesis.outcomeImageNsfw} categoryEmoji={categoryEmoji} hue={hue} size="md" />;
    $[96] = categoryEmoji;
    $[97] = hue;
    $[98] = outcomeLabel;
    $[99] = thesis.marketPubkey;
    $[100] = thesis.outcomeImageNsfw;
    $[101] = thesis.outcomeImageUrl;
    $[102] = thesis.outcomeIndex;
    $[103] = t24;
  } else {
    t24 = $[103];
  }
  let t25;
  if ($[104] !== marketHref || $[105] !== marketIntent || $[106] !== t24) {
    t25 = <Link href={marketHref} prefetch={false} className="relative z-10 shrink-0" {...marketIntent} onClick={_temp5}>{t24}</Link>;
    $[104] = marketHref;
    $[105] = marketIntent;
    $[106] = t24;
    $[107] = t25;
  } else {
    t25 = $[107];
  }
  let t26;
  if ($[108] !== body || $[109] !== metadataRow || $[110] !== tradeLine) {
    t26 = <div className="flex min-w-0 flex-1 flex-col gap-1">{tradeLine}{metadataRow}{body}</div>;
    $[108] = body;
    $[109] = metadataRow;
    $[110] = tradeLine;
    $[111] = t26;
  } else {
    t26 = $[111];
  }
  let t27;
  if ($[112] !== t25 || $[113] !== t26) {
    t27 = <div className="flex gap-3">{t25}{t26}</div>;
    $[112] = t25;
    $[113] = t26;
    $[114] = t27;
  } else {
    t27 = $[114];
  }
  let t28;
  if ($[115] !== isAuthenticated || $[116] !== replyCount || $[117] !== thesis) {
    t28 = <div className="pl-[52px]"><ThesisActions thesis={thesis} replyCount={replyCount} isAuthenticated={isAuthenticated} mode="display" /></div>;
    $[115] = isAuthenticated;
    $[116] = replyCount;
    $[117] = thesis;
    $[118] = t28;
  } else {
    t28 = $[118];
  }
  let t29;
  if ($[119] !== t27 || $[120] !== t28) {
    t29 = <div className="flex flex-col gap-2">{t27}{t28}</div>;
    $[119] = t27;
    $[120] = t28;
    $[121] = t29;
  } else {
    t29 = $[121];
  }
  let t30;
  if ($[122] !== t23 || $[123] !== t29) {
    t30 = <div className="flex flex-col gap-3">{t23}{t29}</div>;
    $[122] = t23;
    $[123] = t29;
    $[124] = t30;
  } else {
    t30 = $[124];
  }
  let t31;
  if ($[125] !== t21 || $[126] !== t22 || $[127] !== t30) {
    t31 = <div id={t21} className="relative rounded-panel-row border border-border-secondary p-3 transition-colors hover:bg-background-tertiary/80">{t22}{t30}</div>;
    $[125] = t21;
    $[126] = t22;
    $[127] = t30;
    $[128] = t31;
  } else {
    t31 = $[128];
  }
  return t31;
}
function _temp5(e_0) {
  e_0.stopPropagation();
}
function _temp4(c_0) {
  return Math.max(0, c_0 - 1);
}
function _temp3(c) {
  return c + 1;
}
function _temp2(e) {
  e.stopPropagation();
}
function _temp(visible) {
  return !visible;
}